import { env } from '@/lib/env';
import { getAccessToken } from '@/lib/authenticated-fetch';

export type StoreCartItem = {
  cartKey: string;
  quantity: number;
  productId: string;
  productName: string;
  productSlug: string;
  productCategory: string;
  variantId: string;
  variantName: string;
  variantPrice: number;
  variantOriginalPrice: number | null;
  variantImage: string;
  variantSku: string;
  size: string | null;
};

type CartResponse = {
  items: StoreCartItem[];
  itemCount: number;
  total: number;
};

type CartMergeResponse = CartResponse & {
  mergedItemCount: number;
  message?: string;
};

type CartClaimResponse = CartResponse & {
  claimedItemCount: number;
  claimStatus: 'claimed' | 'already_claimed' | 'invalid' | 'expired' | 'used' | 'missing_target';
  message?: string;
};

type CartClaimErrorBody = {
  error?: string;
  claimStatus?: CartClaimResponse['claimStatus'];
};

export class CartClaimError extends Error {
  statusCode: number;
  claimStatus?: CartClaimResponse['claimStatus'];

  constructor(message: string, statusCode: number, claimStatus?: CartClaimResponse['claimStatus']) {
    super(message);
    this.name = 'CartClaimError';
    this.statusCode = statusCode;
    this.claimStatus = claimStatus;
  }
}

type CartIdentity = {
  sessionId: string;
};

type AddCartPayload = CartIdentity & {
  productId: string;
  productName: string;
  productSlug: string;
  productCategory: string;
  variantId: string;
  variantName: string;
  variantPrice: number;
  variantOriginalPrice: number | null;
  variantImage: string;
  variantSku: string;
  quantity: number;
  size: string | null;
};

async function getHeaders() {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = await getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

export async function fetchCart(sessionId: string): Promise<CartResponse> {
  const headers = await getHeaders();
  const response = await fetch(`${env.API_URL}/api/cart?sessionId=${encodeURIComponent(sessionId)}`, {
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch cart');
  }

  return response.json();
}

export async function addCartItem(payload: AddCartPayload): Promise<CartResponse> {
  const headers = await getHeaders();
  const response = await fetch(`${env.API_URL}/api/cart/add`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      sessionId: payload.sessionId,
      productId: payload.productId,
      productName: payload.productName,
      productSlug: payload.productSlug,
      productCategory: payload.productCategory,
      variantId: payload.variantId,
      variantName: payload.variantName,
      price: payload.variantPrice,
      variantOriginalPrice: payload.variantOriginalPrice,
      variantImage: payload.variantImage,
      variantSku: payload.variantSku,
      quantity: payload.quantity,
      size: payload.size,
      variantInfo: payload.variantName,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to add cart item');
  }

  return response.json();
}

export async function mergeGuestCart(sessionId: string): Promise<CartMergeResponse> {
  const headers = await getHeaders();
  const response = await fetch(`${env.API_URL}/api/cart/merge`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ sessionId }),
  });

  if (!response.ok) {
    throw new Error('Failed to merge guest cart');
  }

  return response.json() as Promise<CartMergeResponse>;
}

export async function claimCart(token: string, sessionId: string): Promise<CartClaimResponse> {
  const headers = await getHeaders();
  const response = await fetch(`${env.API_URL}/api/cart/claim`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ token, sessionId }),
  });

  if (!response.ok) {
    let errorBody: CartClaimErrorBody = {};
    try {
      errorBody = await response.json() as CartClaimErrorBody;
    } catch {
      errorBody = {};
    }

    throw new CartClaimError(errorBody.error ?? 'Failed to claim cart', response.status, errorBody.claimStatus);
  }

  return response.json() as Promise<CartClaimResponse>;
}

export async function setCartItemQuantity(sessionId: string, productSlug: string, quantity: number, size: string | null) {
  const headers = await getHeaders();
  const response = await fetch(`${env.API_URL}/api/cart/set-quantity`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      sessionId,
      productSlug,
      quantity,
      size,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to update cart quantity');
  }

  return response.json() as Promise<CartResponse>;
}

export async function removeCartItem(sessionId: string, productSlug: string, size: string | null) {
  const headers = await getHeaders();
  const response = await fetch(`${env.API_URL}/api/cart/remove`, {
    method: 'DELETE',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      sessionId,
      productSlug,
      size,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to remove cart item');
  }

  return response.json() as Promise<CartResponse>;
}

export async function clearRemoteCart(sessionId: string) {
  const headers = await getHeaders();
  const response = await fetch(`${env.API_URL}/api/cart/clear`, {
    method: 'DELETE',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      sessionId,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to clear cart');
  }

  return response.json() as Promise<CartResponse>;
}
